import { SpinnerIcon } from "@/components/icons";
import React, { useState, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useStageBasedPosts, useStatusStatistics } from "./hooks/usePosts";
import { StatusStatistics } from "./StatusStatistics";
import { StageFilter } from "./StageFilter";
import { Post, PostStatus } from "@/core/types/postTypes";
import { EditorPostCard } from "./cards/EditorPostCard";
import { ListPostCard } from "./cards/ListPostCard";
import { useAppStore } from "@/core/store/useAppStore";
import {
  getStageConfig,
  getStatisticsOrder,
  PostStage,
} from "@/domains/PostCreator/utils/stageConfig";
import { ShareWithClientModal } from "./ShareWithClientModal";
import { ScheduledPostsList } from "./ScheduledPostsList";
import { TabGroup, TabItem, TabGroupConfig } from "@/components/ui/TabGroup";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";
import { EntityCreateButton, ENTITY_CONFIG } from "@/core/entityConfig";

const PostsViewContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<"grid" | "list" | "scheduled">(() => {
    const viewParam = searchParams.get("view") as "grid" | "list" | "scheduled" | null;
    return viewParam || "grid";
  });
  const [contentType, setContentType] = useState<"all" | "posts" | "reels">(
    "all",
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { setActiveModal, selectedClient } = useAppStore();

  // Fetch statistics independently - these stay consistent
  const {
    statistics,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useStatusStatistics();

  // Fetch posts based on stage
  const {
    posts,
    isLoading: postsLoading,
    error: postsError,
    stageCounts,
    activeStage,
    setActiveStage,
    refetch: refetchPosts,
  } = useStageBasedPosts();

  // Sync URL params with active stage
  useEffect(() => {
    const stageParam = searchParams.get("stage") as PostStage | null;
    if (stageParam && stageParam !== activeStage) {
      setActiveStage(stageParam);
    }
  }, [searchParams, activeStage, setActiveStage]);

  // Sync URL params with view mode (for external URL changes)
  useEffect(() => {
    const viewParam = searchParams.get("view") as "grid" | "list" | "scheduled" | null;
    const targetView = viewParam || "grid";
    if (targetView !== viewMode) {
      setViewMode(targetView);
    }
  }, [searchParams]);

  // Update URL when stage changes
  const handleStageChange = (stage: PostStage) => {
    setActiveStage(stage);
    const params: Record<string, string> = { stage };
    if (viewMode !== "grid") {
      params.view = viewMode;
    }
    setSearchParams(params);
  };

  // Update URL when view mode changes
  const handleViewModeChange = (newViewMode: "grid" | "list" | "scheduled") => {
    setViewMode(newViewMode);
    const params: Record<string, string> = {};
    if (activeStage) {
      params.stage = activeStage;
    }
    if (newViewMode !== "grid") {
      params.view = newViewMode;
    }
    setSearchParams(params);
  };

  // Filter and sort posts by content type and workflow order
  const filteredPosts = posts
    .filter((post) => {
      if (contentType === "all") return true;
      if (contentType === "reels") return post.postType === "reel";
      if (contentType === "posts") return post.postType !== "reel";
      return true;
    })
    .sort((a, b) => {
      const statusOrder = getStatisticsOrder();
      const aIndex = statusOrder.indexOf(a.status);
      const bIndex = statusOrder.indexOf(b.status);

      // If status not found in order, put it at the end
      const aOrder = aIndex === -1 ? statusOrder.length : aIndex;
      const bOrder = bIndex === -1 ? statusOrder.length : bIndex;

      // Primary sort by status order
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // Secondary sort: published posts by date descending, others by date ascending
      const aIsPublished = a.status === "published" || a.status === "partially_published";
      const aDate = a.publishedAt || a.scheduledAt || a.createdAt;
      const bDate = b.publishedAt || b.scheduledAt || b.createdAt;
      if (aIsPublished) {
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      }
      return new Date(aDate).getTime() - new Date(bDate).getTime();
    });

  // Update counts based on filtered posts
  const filteredCount = filteredPosts.length;

  const handleStatusChange = (
    post: Post,
    newStatus: PostStatus,
    response: any,
  ) => {
    console.log("Status updated:", { post: post.id, newStatus, response });

    // Status already updated optimistically by PostStatusDropdown
    // Just refetch stats to keep counts in sync
    refetchStats();
  };

  const handleCreatePost = () => {
    setActiveModal("post-creator");
  };

  const handleRefresh = () => {
    refetchPosts();
    refetchStats();
  };

  // While the store's selectedClient hasn't caught up to the URL client (the
  // resolution window right after navigating to a different/just-created
  // client), the stats/posts hooks transiently run against the previous client
  // and can surface a "Client not found" error. Treat that window as loading,
  // not an error — once the client resolves the hooks refetch cleanly.
  const { clientId: urlClientId } = useParams<{ clientId: string }>();
  const clientResolved = !!selectedClient && selectedClient.id === urlClientId;

  // Main loading state - only when both are loading initially
  const isInitialLoading =
    !clientResolved ||
    (postsLoading && posts.length === 0) ||
    (statsLoading && !statistics);
  const error = clientResolved ? postsError || statsError : null;

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <SpinnerIcon className="w-6 h-6 text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="max-w-md mx-auto mt-16">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Error Loading Posts
            </h3>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Content type tabs
  const contentTypeTabs: TabItem<"all" | "posts" | "reels">[] = [
    { id: "all", label: "Tutti" },
    { id: "posts", label: "Post" },
    { id: "reels", label: "Reel" },
  ];

  const contentTypeConfig: TabGroupConfig = {
    variant: "navigation",
    size: "sm",
    layout: "horizontal",
    hasBackground: true,
  };

  return (
    <PageLayout>
      <PageHeader>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex items-center gap-4 lg:gap-6 overflow-x-auto">
          {/* Stage Filter inline */}
          <StageFilter
            activeStage={activeStage}
            onStageChange={handleStageChange}
            stageCounts={stageCounts}
            isLoading={postsLoading && !posts.length}
          />

          {/* Count Badge */}
          {filteredCount > 0 && (
            <div className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium whitespace-nowrap shrink-0">
              {filteredCount}{" "}
              {contentType === "reels"
                ? filteredCount === 1
                  ? "reel"
                  : "reels"
                : filteredCount === 1
                  ? "post"
                  : "posts"}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Content Type Filter */}
          <div className="inline-block">
            <TabGroup
              items={contentTypeTabs}
              activeId={contentType}
              onTabChange={(id) =>
                setContentType(id as "all" | "posts" | "reels")
              }
              config={contentTypeConfig}
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handleViewModeChange("grid")}
              className={`p-2 rounded-md transition-all duration-200 ${
                viewMode === "grid"
                  ? "bg-white text-gray-900 shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="Grid view"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </button>
            <button
              onClick={() => handleViewModeChange("list")}
              className={`p-2 rounded-md transition-all duration-200 ${
                viewMode === "list"
                  ? "bg-white text-gray-900 shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="List view"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
            <button
              onClick={() => handleViewModeChange("scheduled")}
              className={`p-2 rounded-md transition-all duration-200 ${
                viewMode === "scheduled"
                  ? "bg-white text-gray-900 shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="Scheduled posts"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
          </div>

          {/* Actions */}
          <button
            onClick={handleRefresh}
            disabled={postsLoading || statsLoading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 disabled:opacity-50"
            title="Refresh"
          >
            <svg
              className={`w-5 h-5 ${postsLoading || statsLoading ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>

          {/* Share with Client button - only show when viewing approval stage */}
          {activeStage === "approval_pending" && selectedClient && (
            <button
              onClick={() => setIsShareModalOpen(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 font-medium flex items-center gap-2 shadow-xs hover:shadow-md"
              title="Share approval link with client"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                />
              </svg>
              Share with Client
            </button>
          )}

          <EntityCreateButton
            entity="post"
            onClick={handleCreatePost}
          />
        </div>
        </div>
      </PageHeader>

      <div className="px-4 lg:px-6 pt-4 shrink-0">
        <StatusStatistics
          statistics={statistics}
          isLoading={statsLoading}
          error={statsError}
        />
      </div>

      <PageSection.Canvas>
          {viewMode === "scheduled" ? (
            <div className="py-1">
              {selectedClient ? (
                <ScheduledPostsList
                  clientId={selectedClient.id}
                  onPostClick={(postId) => {
                    // Could navigate to the post if needed
                  }}
                />
              ) : (
                <div className="text-center py-16 text-gray-500">
                  Please select a client to view scheduled posts
                </div>
              )}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              {activeStage && activeStage !== "all" ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">
                      {getStageConfig(activeStage).icon}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    No{" "}
                    {contentType === "reels"
                      ? "reels"
                      : contentType === "posts"
                        ? "posts"
                        : "posts"}{" "}
                    in {getStageConfig(activeStage).label} stage
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    {getStageConfig(activeStage).description}
                  </p>
                  <div className="flex gap-3 justify-center">
                    <EntityCreateButton
                      entity="post"
                      onClick={handleCreatePost}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {posts.length === 0
                      ? contentType === "reels"
                        ? "No reels yet"
                        : "No posts yet"
                      : contentType === "reels"
                        ? "No reels found"
                        : contentType === "posts"
                          ? "No posts found"
                          : "No content found"}
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    {posts.length === 0
                      ? contentType === "reels"
                        ? "Create your first reel to get started with short-form content."
                        : "Create your first post to get started with managing your content."
                      : contentType === "reels"
                        ? "Try switching to 'All' or 'Posts' to see other content."
                        : contentType === "posts"
                          ? "Try switching to 'All' or 'Reels' to see other content."
                          : "Try adjusting your filters to see more content."}
                  </p>
                </>
              )}
            </div>
          ) : (
            <div
              className={viewMode === "grid" ? "py-1" : "space-y-4 py-1"}
              style={viewMode === "grid" ? { display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' } : undefined}
            >
              {filteredPosts.map((post) =>
                viewMode === "list" ? (
                  <ListPostCard
                    key={post.id}
                    post={post}
                    onStatusChange={handleStatusChange}
                    onPostDeleted={() => {
                      refetchPosts();
                      refetchStats();
                    }}
                  />
                ) : (
                  <EditorPostCard
                    key={post.id}
                    post={post}
                    onStatusChange={handleStatusChange}
                    onPostDeleted={() => {
                      refetchPosts();
                      refetchStats();
                    }}
                  />
                )
              )}
            </div>
          )}
      </PageSection.Canvas>

      {/* Loading indicator for background refreshes */}
      {postsLoading && posts.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-white shadow-xs rounded-full px-3 py-2 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="w-3 h-3 bg-blue-500 rounded-full opacity-75"></div>
            <span className="opacity-75">Updating...</span>
          </div>
        </div>
      )}

      {/* Share with Client Modal */}
      {selectedClient && (
        <ShareWithClientModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          clientId={selectedClient.id}
          clientName={selectedClient.clientName}
          redirectPath={`/client/${selectedClient.id}/posts`}
        />
      )}
    </PageLayout>
  );
};

export const PostsView: React.FC = PostsViewContent;
